
import { useState, useCallback, useRef } from "react";
import { User } from "@supabase/supabase-js";
import { useProfileFetching } from "./profile/useProfileFetching";
import { Profile } from "./profile/types";

export const useProfile = () => {
  const [profile, setProfile] = useState<Profile | null>(null);
  const { fetchProfile } = useProfileFetching();
  const isFetchingRef = useRef(false);
  const lastFetchTimeRef = useRef(0);
  const debounceTimeMs = 2000; // 2 seconds debounce
  
  const refreshProfile = useCallback(async (user: User | null) => {
    if (!user) return null;
    
    // Debounce requests - skip if recently fetched
    const now = Date.now();
    if (now - lastFetchTimeRef.current < debounceTimeMs) {
      console.log("Profile refresh debounced - skipping (refreshed recently)");
      return null;
    }
    
    // Prevent concurrent refreshes
    if (isFetchingRef.current) {
      console.log("Profile refresh already in progress - skipping");
      return null;
    }
    
    try {
      isFetchingRef.current = true;
      lastFetchTimeRef.current = now;
      console.log("Refreshing profile for user:", user.id);
      const profileData = await fetchProfile(user.id);
      
      if (profileData) {
        // Ensure email is included from auth user data
        if (!profileData.email && user.email) {
          profileData.email = user.email;
        }
        
        console.log("Updated profile data:", profileData);
        setProfile(profileData);
        return profileData;
      }
      return null;
    } catch (error) {
      console.error("Error refreshing profile:", error);
      return null; // Return null instead of throwing to prevent cascading errors
    } finally {
      isFetchingRef.current = false;
    }
  }, [fetchProfile]);

  return {
    profile,
    setProfile,
    fetchProfile,
    refreshProfile
  };
};
